import { AlertCircle, Check, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatCOP } from "@/lib/format";
import { trpc } from "@/trpc";

// Restated rather than imported: `@agent/*` is a TypeScript-only alias, so the portal cannot
// pull the backend's rule at runtime. MUST stay in sync with MINIMUM_INCOME_TO_RENT_RATIO and
// the rounding in packages/agent/src/helpers/rentGuaranteeIncome.ts — the verdict shown here
// before submitting has to be the one the backend stores.
const MINIMUM_INCOME_TO_RENT_RATIO = 1.7;

// The document types the fianza stores, in Protect's vocabulary. MUST stay in sync with
// LeadRentGuaranteeDocumentType in packages/agent/src/core/lead/types.ts — the endpoint's enum
// rejects anything else. The approval document prints whichever is chosen here, so an asesor
// picking the wrong one puts the wrong document on a legal party.
const DOCUMENT_TYPES = [
  { value: "cc", label: "Cédula de ciudadanía" },
  { value: "ce", label: "Cédula de extranjería" },
  { value: "nit", label: "NIT" },
  { value: "ps", label: "Pasaporte" },
] as const;

type DocumentType = (typeof DOCUMENT_TYPES)[number]["value"];

/** The 1.7x verdict as the backend will compute it, or null while either figure is missing. */
const previewRatio = (
  income: number,
  monthlyRent: number
): { ratio: number; meetsRequirement: boolean } | null => {
  if (!(income > 0) || !(monthlyRent > 0)) return null;
  const ratio = Math.round((income / monthlyRent) * 100) / 100;
  return { ratio, meetsRequirement: ratio >= MINIMUM_INCOME_TO_RENT_RATIO };
};

/** "" and anything that is not a positive integer read as absent — the inputs are integers only. */
const parseAmount = (raw: string): number => {
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : 0;
};

/**
 * Files a fianza request from the lead profile, on the asesor's word.
 *
 * Offered only for a lead with no fianza data on a pipeline that has the fianza positions; the
 * parent decides both. Mirrors what the AI's tool collects — cédula, ingresos, habeas data —
 * with one difference: the asesor names the canon they want approved rather than a listing.
 *
 * The ratio verdict is previewed live and a failing one is a warning, not a block: the request
 * still reaches Palomma either way (as the AI's does), but the asesor should know before sending
 * that no certificate will be issued for that canon, since the fix — a lower canon — is theirs.
 *
 * Collapsed behind a button until asked for: most leads never go through a fianza, and a form
 * on every profile would be noise.
 */
const RentGuaranteeRequestForm = ({
  leadId,
  merchantId,
}: {
  leadId: string;
  merchantId: string;
}) => {
  const utils = trpc.useUtils();
  const [isOpen, setIsOpen] = useState(false);
  const [documentNumber, setDocumentNumber] = useState("");
  // Defaulted rather than empty: the overwhelming majority are cédulas de ciudadanía, and an
  // asesor who never opens the field still files a request that names a real document.
  const [documentType, setDocumentType] = useState<DocumentType>("cc");
  const [incomeRaw, setIncomeRaw] = useState("");
  const [rentRaw, setRentRaw] = useState("");
  const [habeasDataConfirmed, setHabeasDataConfirmed] = useState(false);
  const ids = {
    documentNumber: useId(),
    documentType: useId(),
    income: useId(),
    rent: useId(),
    habeas: useId(),
  };

  const request = trpc.leads.requestRentGuarantee.useMutation({
    onSuccess: async () => {
      toast.success("Solicitud de estudio de fianza enviada.");
      // The Fianza section reads the lead, so the refetch is what swaps this form for the data.
      await utils.leads.get.invalidate({ merchantId, id: leadId });
    },
    onError: (error) => {
      // The endpoint's refusals are written for the asesor (already requested, pipeline without
      // fianza); anything else gets the generic line.
      toast.error(error.message || "No pudimos enviar la solicitud. Intenta de nuevo.");
      console.error("requestRentGuarantee failed", error);
    },
  });

  const income = parseAmount(incomeRaw);
  const requestedMonthlyRent = parseAmount(rentRaw);
  const preview = previewRatio(income, requestedMonthlyRent);
  const canSubmit =
    documentNumber.trim().length > 0 &&
    income > 0 &&
    requestedMonthlyRent > 0 &&
    habeasDataConfirmed &&
    !request.isLoading;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    request.mutate({
      leadId,
      habeasDataConfirmed,
      documentNumber: documentNumber.trim(),
      documentType,
      income,
      requestedMonthlyRent,
    });
  };

  if (!isOpen) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-neutral-600">
          Este lead aún no tiene una solicitud de estudio de fianza.
        </p>
        {/* Outline: opening a form is not the section's primary action, sending it is. */}
        <Button variant="outline" onClick={() => setIsOpen(true)} className="min-h-11 w-full">
          <ShieldCheck />
          Solicitar estudio de fianza
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor={ids.documentType} className="text-xs text-neutral-600">
          Tipo de documento
        </Label>
        <Select
          value={documentType}
          onValueChange={(value) => setDocumentType(value as DocumentType)}
          disabled={request.isLoading}
        >
          <SelectTrigger id={ids.documentType}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DOCUMENT_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label htmlFor={ids.documentNumber} className="text-xs text-neutral-600">
          Número de documento
        </Label>
        <Input
          id={ids.documentNumber}
          autoFocus
          inputMode="numeric"
          autoComplete="off"
          maxLength={40}
          value={documentNumber}
          onChange={(event) => setDocumentNumber(event.target.value)}
          disabled={request.isLoading}
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor={ids.income} className="text-xs text-neutral-600">
          Ingresos del hogar (mensuales)
        </Label>
        <Input
          id={ids.income}
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          placeholder="Ej: 4500000"
          value={incomeRaw}
          onChange={(event) => setIncomeRaw(event.target.value)}
          disabled={request.isLoading}
        />
        <p className="text-xs text-neutral-600">
          Lo que aportan entre todos los que pagarán el canon, no un solo salario.
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor={ids.rent} className="text-xs text-neutral-600">
          Canon solicitado (mensual)
        </Label>
        <Input
          id={ids.rent}
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          placeholder="Ej: 2500000"
          value={rentRaw}
          onChange={(event) => setRentRaw(event.target.value)}
          disabled={request.isLoading}
        />
      </div>

      {preview && (
        // Icon plus text, never colour alone — the verdict has to survive for anyone who cannot
        // distinguish the two states. Same rule as the certificate blocks in PerfilPanel.
        <div className="flex gap-2 text-sm text-neutral-600" role="status" aria-live="polite">
          {preview.meetsRequirement ? (
            <Check className="mt-0.5 size-4 shrink-0" />
          ) : (
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
          )}
          <span>
            {preview.ratio}x sobre {formatCOP(requestedMonthlyRent)} —{" "}
            {preview.meetsRequirement
              ? `cumple el mínimo de ${MINIMUM_INCOME_TO_RENT_RATIO}x.`
              : `no cumple el mínimo de ${MINIMUM_INCOME_TO_RENT_RATIO}x. La solicitud se envía igual, pero no se emitirá certificado para este canon.`}
          </span>
        </div>
      )}

      <div className="flex items-start gap-2">
        <Checkbox
          id={ids.habeas}
          checked={habeasDataConfirmed}
          onCheckedChange={(checked) => setHabeasDataConfirmed(checked === true)}
          disabled={request.isLoading}
          className="mt-0.5"
        />
        <Label htmlFor={ids.habeas} className="text-sm font-normal leading-snug text-neutral-900">
          El cliente autorizó consultar su historial crediticio en centrales de riesgo (habeas
          data).
        </Label>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsOpen(false)}
          disabled={request.isLoading}
          className="min-h-11 flex-1"
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={!canSubmit}
          aria-busy={request.isLoading}
          className="min-h-11 flex-1"
        >
          {request.isLoading ? (
            <Loader2 className="animate-spin motion-reduce:animate-none" />
          ) : null}
          {request.isLoading ? "Enviando…" : "Enviar solicitud"}
        </Button>
      </div>
    </form>
  );
};

export default RentGuaranteeRequestForm;
